import type { Action, History, LocationDescriptor } from "history";
import { useEffect } from "react";
import type { HistoryUnsubscribe } from "react-router-redux";
import { useLocation } from "react-use";

import { useIsValidSidekickClientEmbedOrigin } from "./useIsValidSidekickClientEmbedOrigin";

type SidekickHistoryEvent = {
  type: "sk.historyChanged";
  pathname: string;
  search: string;
  hash: string;
  action: Action;
};

export const useLocationAndHistorySync = (
  history: History & HistoryUnsubscribe,
) => {
  const isValidOrigin = useIsValidSidekickClientEmbedOrigin();
  const location = useLocation();

  // Listen for history changes in Sidekick and update the Metabase location accordingly.
  useEffect(() => {
    const handleSkMessage = async (event: MessageEvent) => {
      const data = event.data as SidekickHistoryEvent;
      if (data.type === "sk.historyChanged") {
        let newPath = data.pathname;
        if (newPath.endsWith("/")) {
          newPath = newPath.slice(0, -1);
        }

        const locationDescriptor: LocationDescriptor = {
          pathname: newPath,
          search: data.search,
          hash: data.hash,
        };

        // Handle history event from Sidekick.
        if (data.action === "REPLACE") {
          history.replace(locationDescriptor);
        } else if (data.action === "PUSH") {
          history.push(locationDescriptor);
        } else if (data.action === "POP") {
          history.goBack();
        }
      }
    };

    window.addEventListener("message", handleSkMessage);
    return () => {
      window.removeEventListener("message", handleSkMessage);
    };
  }, [history, location.pathname, location.search, location.hash]);

  // Listen for history changes in Metabase and inform Sidekick.
  useEffect(() => {
    history.listen((location) => {
      // Don't inform Sidekick of POP actions
      if (isValidOrigin && window.parent !== window && location.action !== "POP") {
        window.parent.postMessage(
          {
            type: "mb.historyChanged",
            ...location,
          },
          window.parent.origin,
        );
      }
    });
    return () => {
      history.unsubscribe();
    };
  }, [history, isValidOrigin]);

  // Listen for search and hash changes in Metabase and inform Sidekick.
  useEffect(() => {
    if (isValidOrigin) {
      window.parent.postMessage(
        {
          type: "mb.searchOrHashChanged",
          search: location.search,
          hash: location.hash,
        },
        window.parent.origin,
      );
    }
  }, [location.search, location.hash, isValidOrigin]);
};
